/**
 * ProDiary.jsx
 * Session 10 — PatchTestChip, ClientSearchField, NewClientDialog extracted
 *              to ProClientComponents.jsx and imported from there.
 *              All other behaviour unchanged from Session 9.
 */

import React, {
  useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayGridPlugin from '@fullcalendar/daygrid';
import {
  Box, Typography, CircularProgress, Alert,
  IconButton, Chip, Divider, Button, MenuItem,
  Select, FormControl, InputLabel, Tooltip,
  Stack, Paper, Snackbar, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid,
} from '@mui/material';
import AddIcon           from '@mui/icons-material/Add';
import CloseIcon         from '@mui/icons-material/Close';
import ChevronLeftIcon   from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon  from '@mui/icons-material/ChevronRight';
import TodayIcon         from '@mui/icons-material/Today';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import SwapHorizIcon     from '@mui/icons-material/SwapHoriz';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon        from '@mui/icons-material/Person';
import PersonAddIcon     from '@mui/icons-material/PersonAdd';
import PhoneIcon         from '@mui/icons-material/Phone';
import EmailIcon         from '@mui/icons-material/Email';
import NoteIcon          from '@mui/icons-material/Note';
import ContentCutIcon    from '@mui/icons-material/ContentCut';
import WifiIcon          from '@mui/icons-material/Wifi';
import RefreshIcon       from '@mui/icons-material/Refresh';
import DeleteIcon        from '@mui/icons-material/Delete';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import OpenInNewIcon     from '@mui/icons-material/OpenInNew';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import api from './axiosConfig';
import {
  PatchTestChip,
  ClientSearchField,
  NewClientDialog,
} from './ProClientComponents';


const FC_LICENSE_KEY = 'CC-Attribution-NonCommercial-NoDerivatives';

const PALETTE = [
  '#4A90D9', '#7B68EE', '#50C878', '#FF8C69',
  '#FFD700', '#20B2AA', '#FF6B9D', '#87CEEB',
];

const STATUS_COLOURS = {
  confirmed:            '#2e7d32',
  awaiting_patch_test:  '#BF360C',
  provisional:          '#BF360C',
  cancelled:            '#757575',
  scheduled:            '#1565c0',
  completed:            '#4CAF50',
  no_show:              '#F44336',
};

const fmtDate = (iso) =>
  new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

const fmtTime = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')}${ampm}`;
};

const toISO = (d) => {
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
};

const addDays = (iso, n) => {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return toISO(d);
};

const stylistColour = (employeeId, overrideHex) =>
  overrideHex || PALETTE[(employeeId || 0) % PALETTE.length];

const toFCEvent = (row, source, stylistMap) => {
  const isOnline      = source === 'online';
  const isProvisional = isOnline && (row.status === 'provisional' || row.status === 'awaiting_patch_test');
  const empId         = row.employee_id;
  const baseColour    = stylistMap[empId]?.colour_hex || stylistColour(empId, row.colour_hex);
  const bgColour      = isOnline ? baseColour + 'BB' : baseColour;
  const borderCol     = isProvisional ? '#FF8C00' : baseColour;
  return {
    id:              `${source}-${row.id}`,
    resourceId:      String(empId),
    title:           row.client_name || '(no name)',
    start:           `${row.appointment_date}T${row.start_time}`,
    end:             `${row.appointment_date}T${row.end_time}`,
    backgroundColor: bgColour,
    borderColor:     borderCol,
    textColor:       '#fff',
    editable:        !['cancelled', 'completed', 'no_show'].includes(row.status),
    extendedProps:   { source, raw: row, isOnline, isProvisional },
  };
};

function EventContent({ eventInfo }) {
  const { isOnline, isProvisional, raw } = eventInfo.event.extendedProps;
  return (
    <Box sx={{
      px: 0.75, py: 0.25, height: '100%', overflow: 'hidden',
      borderLeft: isProvisional ? '3px solid #FF8C00' : 'none',
      outline: isOnline && !isProvisional ? '1.5px dashed rgba(255,255,255,0.65)' : 'none',
      outlineOffset: '-2px', boxSizing: 'border-box',
    }}>
      <Typography variant="caption" sx={{
        display: 'block', fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#fff',
      }}>
        {isProvisional && '⚠ '}
        {isOnline && !isProvisional && '🌐 '}
        {eventInfo.event.title}
      </Typography>
      <Typography variant="caption" sx={{
        display: 'block', fontSize: '0.65rem', opacity: 0.88,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#fff',
      }}>
        {raw.service_name || ''}{raw.service_name ? ' · ' : ''}{eventInfo.timeText}
      </Typography>
    </Box>
  );
}

// ── New Booking Dialog ─────────────────────────────────────────────────────

function NewBookingDialog({ open, onClose, stylists, initialDate, initialTime, initialStylistId, onCreated }) {
  const [services, setServices]           = useState([]);
  const [selectedStylistId, setStylistId] = useState('');
  const [formData, setFormData]           = useState({
    client_name: '', client_phone: '', client_email: '',
    appointment_date: '', start_time: '09:00', notes: '',
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [addedServices, setAddedServices]   = useState([]);
  const [currentSvcId, setCurrentSvcId]     = useState('');
  const [formErrors, setFormErrors]         = useState({});
  const [saving, setSaving]                 = useState(false);
  const [apiError, setApiError]             = useState(null);
  const [newClientOpen, setNewClientOpen]   = useState(false);

  useEffect(() => {
    if (!open) return;
    setStylistId(initialStylistId ? String(initialStylistId) : (stylists[0]?.id ? String(stylists[0].id) : ''));
    setFormData(prev => ({
      ...prev,
      client_name: '', client_phone: '', client_email: '',
      appointment_date: initialDate || toISO(new Date()),
      start_time: initialTime || '09:00',
      notes: '',
    }));
    setSelectedClient(null);
    setAddedServices([]);
    setCurrentSvcId('');
    setFormErrors({});
    setApiError(null);
  }, [open, initialDate, initialTime, initialStylistId, stylists]);

  useEffect(() => {
    if (!selectedStylistId) { setServices([]); return; }

    const token = localStorage.getItem('access_token');
    let accountId = null;
    if (token) {
      try {
        accountId = JSON.parse(atob(token.split('.')[1])).sub?.account_id;
      } catch { /* ignore */ }
    }

    const params = { stylist_id: selectedStylistId, date: formData.appointment_date };
    if (accountId) params.account_id = accountId;

    api.get('/pro-booking/services', { params })
      .then(r => setServices(r.data || []))
      .catch(() => setServices([]));
  }, [selectedStylistId, formData.appointment_date]);

  const handleClientSelected = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({
      ...prev,
      client_name:  client.full_name,
      client_phone: client.phone  || '',
      client_email: client.email  || '',
    }));
    if (formErrors.client_name)  setFormErrors(prev => ({ ...prev, client_name: '' }));
    if (formErrors.client_phone) setFormErrors(prev => ({ ...prev, client_phone: '' }));
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setFormData(prev => ({ ...prev, client_name: '', client_phone: '', client_email: '' }));
  };

  const handleNewClientCreated = (client) => {
    handleClientSelected(client);
  };

  const handleField = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const calcChain = useMemo(() => {
    if (!formData.start_time || !addedServices.length) return [];
    let [h, m] = formData.start_time.split(':').map(Number);
    let cursor = h * 60 + m;
    return addedServices.map(svc => {
      const sStart = `${String(cursor / 60 | 0).padStart(2,'0')}:${String(cursor % 60).padStart(2,'0')}`;
      cursor += svc.duration;
      const sEnd   = `${String(cursor / 60 | 0).padStart(2,'0')}:${String(cursor % 60).padStart(2,'0')}`;
      cursor += (svc.processing_time || 0);
      return { ...svc, sStart, sEnd };
    });
  }, [addedServices, formData.start_time]);

  const totalDuration = useMemo(() =>
    addedServices.reduce((t, s) => t + (s.duration || 0) + (s.processing_time || 0), 0),
    [addedServices]);

  const totalPrice = useMemo(() =>
    addedServices.reduce((t, s) => t + parseFloat(s.current_price || 0), 0),
    [addedServices]);

  const endTime = useMemo(() => {
    if (!formData.start_time || !addedServices.length) return null;
    const [h, m] = formData.start_time.split(':').map(Number);
    const end = h * 60 + m + totalDuration;
    return `${String(end / 60 | 0).padStart(2,'0')}:${String(end % 60).padStart(2,'0')}`;
  }, [formData.start_time, totalDuration]);

  const addService = () => {
    const svc = services.find(s => s.id === parseInt(currentSvcId));
    if (!svc) return;
    setAddedServices(prev => [...prev, svc]);
    setCurrentSvcId('');
    if (formErrors.services) setFormErrors(prev => ({ ...prev, services: '' }));
  };

  const removeService = (idx) =>
    setAddedServices(prev => prev.filter((_, i) => i !== idx));

  const validate = () => {
    const e = {};
    if (!selectedStylistId)              e.stylist      = 'Select a stylist';
    if (!formData.client_name.trim())    e.client_name  = 'Required';
    if (!formData.client_phone.trim())   e.client_phone = 'Required';
    if (!formData.appointment_date)      e.appointment_date = 'Required';
    if (!formData.start_time)            e.start_time   = 'Required';
    if (!addedServices.length)           e.services     = 'Add at least one service';
    setFormErrors(e);
    return !Object.keys(e).length;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setApiError(null);
    try {
      await api.post('/pro-booking/bookings/manual', {
        employee_id:      parseInt(selectedStylistId),
        service_ids:      addedServices.map(s => s.id),
        appointment_date: formData.appointment_date,
        start_time:       formData.start_time,
        client_name:      formData.client_name,
        client_phone:     formData.client_phone,
        client_email:     formData.client_email || undefined,
        client_id:        selectedClient?.id || undefined,
        notes:            formData.notes || undefined,
      });
      onCreated('Booking created successfully!');
      onClose();
    } catch (e) {
      setApiError(e.response?.data?.error || e.message || 'Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>New Appointment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!formErrors.stylist}>
                <InputLabel>Stylist *</InputLabel>
                <Select value={selectedStylistId} label="Stylist *"
                  onChange={e => setStylistId(e.target.value)}>
                  {stylists.map(s => (
                    <MenuItem key={s.id} value={String(s.id)}>
                      {s.first_name} {s.last_name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.stylist && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {formErrors.stylist}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* ── Client section ── */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="subtitle2">Client</Typography>
                  <Button size="small" variant="outlined" startIcon={<PersonAddIcon />}
                    onClick={() => setNewClientOpen(true)}>
                    New client
                  </Button>
                </Box>

                {selectedClient ? (
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 1, bgcolor: '#e8f5e9', borderColor: '#a5d6a7' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <PersonIcon fontSize="small" color="success" />
                        <Typography variant="body2" fontWeight={600}>{selectedClient.full_name}</Typography>
                        <Typography variant="body2" color="text.secondary">{selectedClient.phone}</Typography>
                        <PatchTestChip status={selectedClient.patch_test?.status} />
                      </Box>
                      <Tooltip title="Clear client selection">
                        <IconButton size="small" onClick={handleClearClient}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {selectedClient.allergies && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#c62828', fontWeight: 600 }}>
                        ⚠ Allergies: {selectedClient.allergies}
                      </Typography>
                    )}
                  </Paper>
                ) : (
                  <Box sx={{ mb: 1.5 }}>
                    <ClientSearchField onClientSelected={handleClientSelected} />
                  </Box>
                )}

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Client name *" name="client_name"
                      value={formData.client_name} onChange={handleField}
                      error={!!formErrors.client_name} helperText={formErrors.client_name}
                      size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Phone *" name="client_phone"
                      value={formData.client_phone} onChange={handleField}
                      error={!!formErrors.client_phone} helperText={formErrors.client_phone}
                      size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Email" name="client_email"
                      value={formData.client_email} onChange={handleField}
                      type="email" size="small" />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Date / time */}
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Date *" name="appointment_date" type="date"
                value={formData.appointment_date} onChange={handleField}
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.appointment_date} helperText={formErrors.appointment_date} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Start time *" name="start_time" type="time"
                value={formData.start_time} onChange={handleField}
                InputLabelProps={{ shrink: true }}
                error={!!formErrors.start_time} helperText={formErrors.start_time} />
            </Grid>

            {/* Services */}
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Services</Typography>
                {calcChain.map((svc, idx) => (
                  <Paper key={idx} sx={{ p: 2, mb: 1.5 }}>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item xs={1}>
                        <Typography variant="h6" color="primary">{idx + 1}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body1" fontWeight="bold">{svc.name}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          {svc.duration}min work
                          {svc.processing_time > 0 && ` + ${svc.processing_time}min processing`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtTime(svc.sStart)} → {fmtTime(svc.sEnd)}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body1">£{parseFloat(svc.current_price || 0).toFixed(2)}</Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton size="small" color="error" onClick={() => removeService(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                    {svc.requires_patch_test && (
                      <Chip label="Patch test required" size="small"
                        icon={<WarningAmberIcon style={{ fontSize: 14 }} />}
                        sx={{ mt: 1, bgcolor: '#fff3e0', color: '#BF360C', fontWeight: 600 }} />
                    )}
                  </Paper>
                ))}
                {addedServices.length > 0 && (
                  <Box sx={{ p: 1.5, bgcolor: 'primary.light', borderRadius: 1, mb: 1.5 }}>
                    <Grid container>
                      <Grid item xs={6}>
                        <Typography variant="body2">Total: {totalDuration} mins</Typography>
                      </Grid>
                      <Grid item xs={6} textAlign="right">
                        <Typography variant="body2" fontWeight="bold">
                          £{totalPrice.toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                    {endTime && (
                      <Typography variant="caption" color="text.secondary">
                        Finishes at {fmtTime(endTime)}
                      </Typography>
                    )}
                  </Box>
                )}
                {formErrors.services && (
                  <Alert severity="error" sx={{ mb: 1.5 }}>{formErrors.services}</Alert>
                )}
                <Box sx={{ p: 2, border: '2px dashed #ccc', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Add service</Typography>
                  <FormControl fullWidth sx={{ mb: 1.5 }}>
                    <InputLabel>Select service</InputLabel>
                    <Select value={currentSvcId} label="Select service"
                      onChange={e => setCurrentSvcId(e.target.value)}
                      disabled={!selectedStylistId}>
                      {services.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                          {s.name} — £{parseFloat(s.current_price || 0).toFixed(2)} ({s.duration}min
                          {s.processing_time > 0 ? ` + ${s.processing_time}min` : ''})
                          {s.requires_patch_test ? ' ⚠' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!selectedStylistId && (
                    <Typography variant="caption" color="text.secondary">
                      Select a stylist first to see their services
                    </Typography>
                  )}
                  <Button variant="contained" startIcon={<AddIcon />}
                    onClick={addService} disabled={!currentSvcId} fullWidth>
                    Add this service
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Notes" name="notes"
                value={formData.notes} onChange={handleField}
                multiline rows={3} />
            </Grid>
            {apiError && (
              <Grid item xs={12}>
                <Alert severity="error">{apiError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {saving ? 'Creating…' : 'Create Booking'}
          </Button>
        </DialogActions>
      </Dialog>

      <NewClientDialog
        open={newClientOpen}
        onClose={() => setNewClientOpen(false)}
        onCreated={handleNewClientCreated}
      />
    </>
  );
}

// ── Price-change prompt (moving a booking to a stylist on a different tier) ─

function RepriceDialog({ open, changes, busy, onKeep, onReprice, onCancel }) {
  const list = changes || [];
  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Price change</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This stylist charges a different price. Which price should the client pay?
        </Alert>
        {list.map((c, i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography variant="body2">{c.service_name}</Typography>
            <Typography variant="body2">
              <span style={{ textDecoration: 'line-through', color: '#9e9e9e' }}>
                £{Number(c.old_price).toFixed(2)}
              </span>
              {' → '}
              <strong>£{Number(c.new_price).toFixed(2)}</strong>
            </Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={busy} color="inherit">Cancel move</Button>
        <Button onClick={onKeep} disabled={busy} variant="outlined">Keep booked price</Button>
        <Button onClick={onReprice} disabled={busy} variant="contained">Re-price</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Appointment Detail Dialog ──────────────────────────────────────────────

function AppointmentDialog({ event, stylists, open, onClose, onReassigned, onCancelled }) {
  // routerNavigate: React Router navigation (page transitions)
  // navigate():     local function for diary prev/next day — DO NOT CONFUSE
  const routerNavigate = useNavigate();
  const [reassigning, setReassigning]         = useState(false);
  const [newStylistId, setNewStylist]         = useState('');
  const [saving, setSaving]                   = useState(false);
  const [dialogError, setDialogError]         = useState(null);
  const [cancelConfirmOpen, setCancelConfirm] = useState(false);
  const [priceChange, setPriceChange]         = useState(null);

  useEffect(() => {
    setReassigning(false);
    setNewStylist('');
    setDialogError(null);
    setPriceChange(null);
  }, [event?.id]);

  if (!event) return null;

  const { raw, isOnline, isProvisional } = event.extendedProps;
  const source = raw.source || (isOnline ? 'online' : 'manual');
  const statusColour = STATUS_COLOURS[raw.status] || '#555';
  const canCheckout = !['cancelled', 'completed', 'no_show'].includes(raw.status);

  const handleCheckout = () => {
    onClose();
    routerNavigate(`/pro/record-sale/${raw.id}/${source}`);
  };

  const handleViewClient = () => {
    onClose();
    routerNavigate(`/pro/clients/${raw.client_id}`);
  };

  // reprice: undefined = first attempt (the server asks if the price would change),
  //          true = re-price to the new stylist's price, false = keep the booked price
  const doReassign = async (reprice) => {
    if (!newStylistId) return;
    setSaving(true);
    setDialogError(null);
    try {
      const body = { stylist_id: Number(newStylistId) };
      if (reprice !== undefined) body.reprice = reprice;
      await api.put(`/pro-booking/bookings/${raw.id}/reassign`, body);
      setPriceChange(null);
      onReassigned();
      onClose();
    } catch (e) {
      const d = e.response?.data;
      if (e.response?.status === 409 && d?.price_change_required) {
        setPriceChange([{
          service_name: d.service_name || raw.service_name || 'Service',
          old_price:    d.old_price,
          new_price:    d.new_price,
        }]);
      } else {
        setDialogError(d?.error || e.message || 'Reassign failed');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleReassign = () => doReassign(undefined);

  const handleCancelConfirm = async () => {
    setSaving(true);
    try {
      await api.post(`/pro-diary/bookings/${raw.id}/cancel`, { source });
      setCancelConfirm(false);
      onCancelled('Booking cancelled');
      onClose();
    } catch (e) {
      setDialogError(e.response?.data?.error || e.message || 'Cancel failed');
      setCancelConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isOnline && <Tooltip title="Online booking"><WifiIcon fontSize="small" color="primary" /></Tooltip>}
              {isProvisional && <Tooltip title="Patch test required"><WarningAmberIcon fontSize="small" sx={{ color: '#BF360C' }} /></Tooltip>}
              <Typography variant="h6">Appointment Details</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label={raw.status} size="small"
                sx={{ bgcolor: statusColour, color: '#fff', fontWeight: 600, textTransform: 'capitalize' }} />
              <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 1 }}>
          {/* ── Allergy Warning ─────────────────────────────────────────── */}
    {raw.allergies && (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5, mb: 2, borderRadius: 2,
          bgcolor: '#fff3e0',
          borderColor: '#e53935',
          borderWidth: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#e53935', mt: 0.2, flexShrink: 0 }} />
          <Box>
            <Typography variant="caption" sx={{
              fontSize: 10, textTransform: 'uppercase',
              letterSpacing: 0.5, fontWeight: 700, color: '#e53935'
            }}>
              Allergy / Sensitivity Alert
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25, color: '#b71c1c', fontWeight: 500 }}>
              {raw.allergies}
            </Typography>
          </Box>
        </Box>
      </Paper>
    )}
    {/* ────────────────────────────────────────────────────────────── */}
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <CalendarMonthIcon fontSize="small" color="action" />
                <Typography variant="body2" fontWeight={600}>{fmtDate(raw.appointment_date)}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                {fmtTime(raw.start_time)} – {fmtTime(raw.end_time)}
              </Typography>
            </Paper>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <ContentCutIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Service</Typography>
                <Typography variant="body2" fontWeight={600}>{raw.service_name || 'Appointment'}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <PersonIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Stylist</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {`${raw.first_name || ''} ${raw.last_name || ''}`.trim() || '—'}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Client block — with "View client card" link if client_id exists */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <PersonIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Client</Typography>
                  <Typography variant="body2" fontWeight={600}>{raw.client_name}</Typography>
                </Box>
              </Box>
              {raw.client_id && (
                <Tooltip title="Open client card">
                  <Button size="small" variant="outlined" startIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={handleViewClient}
                    sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0 }}>
                    Client card
                  </Button>
                </Tooltip>
              )}
            </Box>

            {raw.client_phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="body2" component="a" href={`tel:${raw.client_phone}`}
                  sx={{ color: 'primary.main', textDecoration: 'none' }}>{raw.client_phone}</Typography>
              </Box>
            )}
            {raw.client_email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2" component="a" href={`mailto:${raw.client_email}`}
                  sx={{ color: 'primary.main', textDecoration: 'none' }}>{raw.client_email}</Typography>
              </Box>
            )}

            {raw.notes && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <NoteIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Notes</Typography>
                    <Typography variant="body2">{raw.notes}</Typography>
                  </Box>
                </Box>
              </>
            )}

            {isProvisional && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: '#BF360C', bgcolor: '#fff3e0' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                    <WarningAmberIcon fontSize="small" sx={{ color: '#BF360C' }} />
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#BF360C' }}>
                      Patch test required
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    This booking is provisional until a patch test is recorded.
                    {raw.client_id && ' Open the client card to record a patch test.'}
                  </Typography>
                </Paper>
              </>
            )}

            {isOnline && canCheckout && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SwapHorizIcon fontSize="small" /> Reassign stylist
                </Typography>
                {!reassigning ? (
                  <Button variant="outlined" size="small" startIcon={<SwapHorizIcon />}
                    onClick={() => setReassigning(true)}>
                    Move to different stylist
                  </Button>
                ) : (
                  <Stack spacing={1.5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>New stylist</InputLabel>
                      <Select value={newStylistId} label="New stylist"
                        onChange={e => setNewStylist(e.target.value)}>
                        {stylists.filter(s => s.id !== raw.employee_id).map(s => (
                          <MenuItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {dialogError && <Alert severity="error" sx={{ py: 0 }}>{dialogError}</Alert>}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="contained" size="small" disabled={!newStylistId || saving}
                        onClick={handleReassign}
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}>
                        {saving ? 'Saving…' : 'Confirm reassign'}
                      </Button>
                      <Button variant="outlined" size="small"
                        onClick={() => { setReassigning(false); setNewStylist(''); setDialogError(null); }}>
                        Cancel
                      </Button>
                    </Box>
                  </Stack>
                )}
              </>
            )}

            {dialogError && !reassigning && (
              <Alert severity="error" sx={{ mt: 2 }}>{dialogError}</Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box>
            {canCheckout && (
              <Button onClick={() => setCancelConfirm(true)} color="error" variant="outlined" size="small"
                disabled={saving}>
                Cancel appointment
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canCheckout && (
              <Button onClick={handleCheckout} variant="contained" color="success" size="small"
                startIcon={<ShoppingCartCheckoutIcon />}>
                Checkout
              </Button>
            )}
            {raw.status === 'completed' && (
              <Chip icon={<CheckCircleIcon />} label="Completed" color="success" size="small" />
            )}
          </Box>
        </DialogActions>
      </Dialog>

      <RepriceDialog
        open={!!priceChange}
        changes={priceChange}
        busy={saving}
        onKeep={() => doReassign(false)}
        onReprice={() => doReassign(true)}
        onCancel={() => setPriceChange(null)}
      />

      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel this appointment?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" fontWeight={500} sx={{ mb: 0.5 }}>
            {raw.client_name}
            {raw.service_name && ` · ${raw.service_name}`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {raw.appointment_date} at {fmtTime(raw.start_time)}
          </Typography>
          <Alert severity="warning">
            This cannot be undone. The client will not be automatically notified.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirm(false)} variant="contained" disabled={saving}>
            Keep appointment
          </Button>
          <Button onClick={handleCancelConfirm} color="error" variant="outlined" disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}>
            {saving ? 'Cancelling…' : 'Yes, cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Main ProDiary component ────────────────────────────────────────────────

export default function ProDiary() {
  const calRef = useRef(null);
  const routerNavigate = useNavigate();

  const [currentDate, setCurrentDate]       = useState(toISO(new Date()));
  const [viewMode, setViewMode]             = useState('resourceTimeGridDay');
  const [stylists, setStylists]             = useState([]);
  const [events, setEvents]                 = useState([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState(null);
  const [selectedEvent, setSelectedEvent]   = useState(null);
  const [detailOpen, setDetailOpen]         = useState(false);
  const [snackbar, setSnackbar]             = useState({ open: false, message: '', severity: 'success' });
  const [pendingDrop, setPendingDrop]       = useState(null);   // drag that needs a price decision
  const [dropBusy, setDropBusy]             = useState(false);

  const [newBookingOpen, setNewBookingOpen]             = useState(false);
  const [newBookingDate, setNewBookingDate]             = useState(null);
  const [newBookingTime, setNewBookingTime]             = useState(null);
  const [newBookingStylistId, setNewBookingStylistId]   = useState(null);

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const stylistMap = useMemo(
    () => Object.fromEntries(stylists.map(s => [s.id, s])),
    [stylists],
  );

  useEffect(() => {
    api.get('/pro-diary/stylists')
      .then(r => setStylists(r.data || []))
      .catch(e => setError(e.response?.data?.error || e.message));
  }, []);

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (viewMode === 'resourceTimeGridDay') {
      return { rangeStart: currentDate, rangeEnd: currentDate };
    }
    const d   = new Date(currentDate + 'T00:00:00');
    const dow = d.getDay();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((dow + 6) % 7));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return { rangeStart: toISO(mon), rangeEnd: toISO(sun) };
  }, [currentDate, viewMode]);

  const loadEvents = useCallback(async () => {
    if (!stylists.length) return;
    setLoading(true);
    setError(null);
    try {
      const [manualRes, onlineRes] = await Promise.all([
        api.get('/pro-diary/appointments', { params: { start: rangeStart, end: rangeEnd } }),
        api.get('/pro-booking/bookings',   { params: { start: rangeStart, end: rangeEnd } }),
      ]);
      setEvents([
        ...(manualRes.data || []).map(r => toFCEvent(r, 'manual', stylistMap)),
        ...(onlineRes.data || []).map(r => toFCEvent(r, 'online', stylistMap)),
      ]);
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load diary';
      setError(msg);
      showSnackbar(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [rangeStart, rangeEnd, stylistMap, stylists.length]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const resources = useMemo(() =>
    stylists.map(s => ({ id: String(s.id), title: `${s.first_name} ${s.last_name}`.trim() })),
    [stylists],
  );

  // navigate(): diary prev/next day — NOT React Router navigation
  const navigate = (dir) => {
    const step = viewMode === 'resourceTimeGridDay' ? 1 : 7;
    const next = addDays(currentDate, dir * step);
    setCurrentDate(next);
    calRef.current?.getApi().gotoDate(next);
  };

  const goToday = () => {
    const today = toISO(new Date());
    setCurrentDate(today);
    calRef.current?.getApi().gotoDate(today);
  };
  
  const handleEventDrop = useCallback(async (dropInfo) => {
  const { event, revert } = dropInfo;
  const { source, raw, isOnline, isProvisional } = event.extendedProps;
  const newStart      = event.start;
  const newEnd        = event.end;
  const newResourceId = dropInfo.newResource?.id;
  const pad    = (n) => String(n).padStart(2, '0');
  const fmtHHMM = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const fmtYMD  = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const payload = {
    source,
    appointment_date: fmtYMD(newStart),
    start_time:       fmtHHMM(newStart),
    end_time:         fmtHHMM(newEnd),
  };
  if (newResourceId) {
    payload.employee_id = parseInt(newResourceId);
  }

  try {
    await api.put(`/pro-diary/bookings/${raw.id}/reschedule`, payload);

    // Patch the event colour immediately so FullCalendar doesn't flash
    // the wrong palette colour while loadEvents() is in-flight.
    const empId     = newResourceId ? parseInt(newResourceId) : raw.employee_id;
    const baseColour = stylistMap[empId]?.colour_hex || stylistColour(empId, null);
    event.setProp('backgroundColor', isOnline ? baseColour + 'BB' : baseColour);
    event.setProp('borderColor', isProvisional ? '#FF8C00' : baseColour);

    showSnackbar('Booking rescheduled');
    loadEvents();
  } catch (e) {
    const d = e.response?.data;
    if (e.response?.status === 409 && d?.price_change_required) {
      // Moving to a stylist on a different tier changes the price: ask first.
      setPendingDrop({ dropInfo, payload, changes: d.changes || [] });
      return;
    }
    revert();
    showSnackbar(d?.error || 'Reschedule failed', 'error');
  }
}, [loadEvents, stylistMap]);

  const finishDrop = async (reprice) => {
    if (!pendingDrop) return;
    const { dropInfo, payload } = pendingDrop;
    setDropBusy(true);
    try {
      await api.put(`/pro-diary/bookings/${dropInfo.event.extendedProps.raw.id}/reschedule`,
        { ...payload, reprice });
      setPendingDrop(null);
      showSnackbar(reprice ? 'Booking moved and re-priced' : 'Booking moved — booked price kept');
      loadEvents();
    } catch (e) {
      dropInfo.revert();
      setPendingDrop(null);
      showSnackbar(e.response?.data?.error || 'Reschedule failed', 'error');
    } finally {
      setDropBusy(false);
    }
  };

  const cancelDrop = () => {
    pendingDrop?.dropInfo.revert();
    setPendingDrop(null);
  };

  const handleDateClick = (arg) => {
    setNewBookingDate(toISO(arg.date));
    setNewBookingTime(arg.date.toTimeString().substring(0, 5));
    setNewBookingStylistId(arg.resource?.id ? parseInt(arg.resource.id) : null);
    setNewBookingOpen(true);
  };

  const toolbarLabel = useMemo(() => {
    if (viewMode === 'resourceTimeGridDay') return fmtDate(currentDate);
    const s = new Date(rangeStart + 'T00:00:00');
    const e = new Date(rangeEnd   + 'T00:00:00');
    return `${s.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – `
         + `${e.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }, [currentDate, viewMode, rangeStart, rangeEnd]);

  const { countOnline, countManual, countPatch } = useMemo(() => ({
    countOnline: events.filter(e => e.extendedProps.isOnline).length,
    countManual: events.filter(e => !e.extendedProps.isOnline).length,
    countPatch:  events.filter(e => e.extendedProps.isProvisional).length,
  }), [events]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>

      {/* Toolbar */}
      <Box sx={{
        bgcolor: 'white', borderBottom: '1px solid #e0e0e0',
        px: 2, py: 1.25, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
        flexShrink: 0,
      }}>
        <IconButton size="small" onClick={() => navigate(-1)}><ChevronLeftIcon /></IconButton>
        <Tooltip title="Today">
          <IconButton size="small" onClick={goToday}><TodayIcon fontSize="small" /></IconButton>
        </Tooltip>
        <IconButton size="small" onClick={() => navigate(1)}><ChevronRightIcon /></IconButton>

        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, minWidth: 160 }}>
          {toolbarLabel}
        </Typography>

        {[
          { key: 'resourceTimeGridDay', label: 'Day'  },
          { key: 'timeGridWeek',        label: 'Week' },
        ].map(v => (
          <Button key={v.key} size="small"
            variant={viewMode === v.key ? 'contained' : 'outlined'}
            onClick={() => setViewMode(v.key)}
            sx={{ minWidth: 54 }}>
            {v.label}
          </Button>
        ))}

        <Button variant="outlined" size="small" startIcon={<PointOfSaleIcon />}
          onClick={() => routerNavigate('/pro/till-reconciliation')}
          sx={{ borderColor: '#2f3542', color: '#2f3542', '&:hover': { bgcolor: '#f0f0f0' } }}>
          Cash Up
        </Button>

        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadEvents} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Legend */}
      <Box sx={{
        bgcolor: 'white', px: 2, py: 0.75, borderBottom: '1px solid #e0e0e0',
        display: 'flex', gap: 2.5, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#4A90D9' }} />
          <Typography variant="caption" color="text.secondary">Manual ({countManual})</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#4A90D9BB',
            outline: '1.5px dashed #4A90D9', outlineOffset: '-1px' }} />
          <Typography variant="caption" color="text.secondary">Online ({countOnline})</Typography>
        </Box>
        {countPatch > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningAmberIcon sx={{ fontSize: 14, color: '#BF360C' }} />
            <Typography variant="caption" sx={{ color: '#BF360C', fontWeight: 600 }}>
              Patch test pending ({countPatch})
            </Typography>
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontStyle: 'italic' }}>
          Click any time slot to add a booking
        </Typography>
        {error && <Alert severity="error" sx={{ py: 0, flex: 1 }}>{error}</Alert>}
      </Box>

      {/* Calendar */}
      <Box sx={{ flex: 1, minHeight: 0, p: 1 }}>
        <FullCalendar
          key={viewMode}
          ref={calRef}
          plugins={[resourceTimeGridPlugin, interactionPlugin, dayGridPlugin]}
          schedulerLicenseKey={FC_LICENSE_KEY}
          initialView={viewMode}
          initialDate={currentDate}
          resources={resources}
          events={events}
          headerToolbar={false}
          locale="en-GB"
          firstDay={1}
          slotMinTime="07:00:00"
          slotMaxTime="22:00:00"
          slotDuration="00:15:00"
          slotLabelInterval="01:00:00"
          slotLabelFormat={{ hour: 'numeric', minute: '2-digit', hour12: true }}
          eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
          allDaySlot={false}
          nowIndicator
          expandRows
          height="100%"
          selectable
          dateClick={handleDateClick}
          resourceAreaHeaderContent="Stylist"
          resourceAreaWidth={110}
          eventContent={(info) => <EventContent eventInfo={info} />}
          eventClick={({ event }) => {
            setSelectedEvent(event);
            setDetailOpen(true);
          }}
          editable
          eventResourceEditable
          eventDrop={handleEventDrop}
          datesSet={({ startStr }) => setCurrentDate(startStr.split('T')[0])}
        />
          
      </Box>

      <NewBookingDialog
        open={newBookingOpen}
        onClose={() => setNewBookingOpen(false)}
        stylists={stylists}
        initialDate={newBookingDate}
        initialTime={newBookingTime}
        initialStylistId={newBookingStylistId}
        onCreated={(msg) => { showSnackbar(msg); loadEvents(); }}
      />

      <AppointmentDialog
        open={detailOpen}
        event={selectedEvent}
        stylists={stylists}
        onClose={() => { setDetailOpen(false); setSelectedEvent(null); }}
        onReassigned={() => { showSnackbar('Booking reassigned successfully'); loadEvents(); }}
        onCancelled={(msg) => { showSnackbar(msg); loadEvents(); }}
      />

      <RepriceDialog
        open={!!pendingDrop}
        changes={pendingDrop?.changes}
        busy={dropBusy}
        onKeep={() => finishDrop(false)}
        onReprice={() => finishDrop(true)}
        onCancel={cancelDrop}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity}
          onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}